import { canEditBoard, DomainError, newId, SAVE_DEBOUNCE_MS, type BoardAccess, type WorkflowGraph } from '@blueprint/domain'
import { create } from 'zustand'
import { RepositoryError } from '../data/index.js'
import { getRepository, refreshBoardList, targetProjectId, useBoardStore } from './board-store.js'

export type WorkflowPhase = 'idle' | 'loading' | 'ready' | 'missing' | 'error'
export type WorkflowSaveStatus = 'saved' | 'pending' | 'saving' | 'error' | 'conflict'

interface WorkflowStore {
  phase: WorkflowPhase
  id: string | null
  name: string
  access: BoardAccess
  graph: WorkflowGraph | null
  /** The server revision the next save is made against. */
  revision: number
  saveStatus: WorkflowSaveStatus
  saveError: string | null
  lastSavedAt: string | null
  /** Bumped by every load from the server, so the editor knows to take the graph afresh. */
  loads: number
  canUndo: boolean
  canRedo: boolean
  /** A node to select once the workflow is open, e.g. when arriving from a storymap card. */
  focusNodeId: string | null

  open: (id: string, focusNodeId?: string | null) => Promise<void>
  /** Creates and opens a workflow. Resolves to the new id. */
  create: (name: string) => Promise<string | null>
  /**
   * Every committed edit: the whole graph, optionally with a new name. Saved debounced.
   * Consecutive changes with the same `group` are one undo step, e.g. a word typed into
   * one field, or a node and its connections removed by one Delete.
   */
  change: (graph: WorkflowGraph, name?: string, group?: string) => void
  /** Steps the graph back, or forward again, and saves it. Resolves to the graph now shown; null when there was none. */
  undo: () => WorkflowGraph | null
  redo: () => WorkflowGraph | null
  /** Waits for every pending change to reach the server. */
  flush: () => Promise<void>
  retry: () => void
  /** After a conflict: drop local changes and load the server's version. */
  reload: () => Promise<void>
  leave: () => Promise<void>
  clearFocus: () => void
}

function describe(error: unknown): string {
  if (error instanceof RepositoryError || error instanceof DomainError || error instanceof Error) return error.message
  return 'Something went wrong.'
}

let timer: ReturnType<typeof setTimeout> | null = null
let inFlight: Promise<void> | null = null
/** Set by every change; cleared when that state has been sent. */
let dirty = false
/** Whether the name changed since the last save, so an unchanged name is not resent. */
let nameDirty = false

/** As many steps back as the mindmap keeps. The name is not part of the history, as on the mindmap. */
const UNDO_DEPTH = 50
let undoStack: WorkflowGraph[] = []
let redoStack: WorkflowGraph[] = []
/** The group of the last change, so the next change in it adds to that step. */
let lastGroup: string | null = null

function clearHistory(): { canUndo: false; canRedo: false } {
  undoStack = []
  redoStack = []
  lastGroup = null
  return { canUndo: false, canRedo: false }
}

function clearTimer(): void {
  if (timer) clearTimeout(timer)
  timer = null
}

/**
 * One save at a time, always of the latest graph. Changes made while a save is in flight
 * are sent by the next pass of the loop, against the revision the first one returned.
 */
async function saveLoop(): Promise<void> {
  const store = useWorkflowStore
  while (dirty) {
    const { id, graph, name, revision } = store.getState()
    if (!id || !graph) return
    dirty = false
    const sendName = nameDirty
    nameDirty = false
    store.setState({ saveStatus: 'saving', saveError: null })
    try {
      const saved = await getRepository().saveWorkflow(id, { expectedRevision: revision, graph, ...(sendName ? { name } : {}) })
      if (store.getState().id !== id) return
      store.setState({ revision: saved.revision, lastSavedAt: saved.updatedAt })
      if (sendName) void refreshBoardList().catch(() => undefined)
    } catch (error) {
      if (store.getState().id !== id) return
      // The change is still unsaved: keep it marked so a retry sends it.
      dirty = true
      nameDirty ||= sendName
      const conflict = error instanceof RepositoryError && error.kind === 'conflict'
      store.setState({ saveStatus: conflict ? 'conflict' : 'error', saveError: describe(error) })
      return
    }
  }
  store.setState({ saveStatus: 'saved' })
}

function startSave(): Promise<void> {
  clearTimer()
  inFlight ??= saveLoop().finally(() => {
    inFlight = null
  })
  return inFlight
}

/** Shows the graph, optionally with a new name, and saves it debounced. */
function send(graph: WorkflowGraph, name?: string): void {
  const store = useWorkflowStore
  const renamed = name !== undefined && name !== store.getState().name
  store.setState({ graph, ...(renamed ? { name } : {}), saveStatus: store.getState().saveStatus === 'conflict' ? 'conflict' : 'pending' })
  dirty = true
  nameDirty ||= renamed
  // A conflict waits for the user's choice; nothing more is sent until then.
  if (store.getState().saveStatus === 'conflict') return
  clearTimer()
  timer = setTimeout(() => void startSave(), SAVE_DEBOUNCE_MS)
}

export const useWorkflowStore = create<WorkflowStore>((set, get) => ({
  phase: 'idle',
  id: null,
  name: '',
  access: 'owner',
  graph: null,
  revision: 0,
  saveStatus: 'saved',
  saveError: null,
  lastSavedAt: null,
  loads: 0,
  canUndo: false,
  canRedo: false,
  focusNodeId: null,

  async open(id, focusNodeId = null) {
    if (get().id === id && get().phase === 'ready') {
      if (focusNodeId) set({ focusNodeId })
      return
    }
    await get().leave()
    set({ phase: 'loading', id, graph: null, saveStatus: 'saved', saveError: null, focusNodeId })
    try {
      const workflow = await getRepository().getWorkflow(id)
      if (get().id !== id) return
      set({
        phase: 'ready',
        name: workflow.name,
        access: workflow.access,
        graph: workflow.graph,
        revision: workflow.revision,
        lastSavedAt: workflow.updatedAt,
        loads: get().loads + 1,
        // A load from the server starts a fresh history, as on the mindmap.
        ...clearHistory(),
      })
    } catch (error) {
      if (get().id !== id) return
      const missing = error instanceof RepositoryError && error.kind === 'not_found'
      set({ phase: missing ? 'missing' : 'error', saveError: missing ? null : describe(error) })
    }
  },

  async create(name) {
    try {
      const projectId = targetProjectId()
      const created = await getRepository().createWorkflow({ id: newId(), name: name.trim() || 'New workflow', ...(projectId ? { projectId } : {}) })
      // The project list too: its item counts changed.
      await useBoardStore.getState().loadProjects()
      await get().open(created.id)
      return created.id
    } catch (error) {
      set({ saveError: describe(error) })
      return null
    }
  },

  change(graph, name, group) {
    if (!get().id || !canEditBoard(get().access)) return
    // Only a change to the graph is a step to undo; a rename alone or an unchanged graph is not.
    const previous = get().graph
    if (previous && JSON.stringify(previous) !== JSON.stringify(graph)) {
      // A change in the same group as the last one belongs to its step.
      if (!group || group !== lastGroup) {
        undoStack.push(previous)
        if (undoStack.length > UNDO_DEPTH) undoStack.shift()
      }
      redoStack = []
      lastGroup = group ?? null
      set({ canUndo: true, canRedo: false })
    }
    send(graph, name)
  },

  undo() {
    const current = get().graph
    if (!get().id || !current || !canEditBoard(get().access) || undoStack.length === 0) return null
    const previous = undoStack.pop()!
    redoStack.push(current)
    lastGroup = null
    set({ canUndo: undoStack.length > 0, canRedo: true })
    send(previous)
    return previous
  },

  redo() {
    const current = get().graph
    if (!get().id || !current || !canEditBoard(get().access) || redoStack.length === 0) return null
    const next = redoStack.pop()!
    undoStack.push(current)
    lastGroup = null
    set({ canUndo: true, canRedo: redoStack.length > 0 })
    send(next)
    return next
  },

  async flush() {
    if (!dirty && !inFlight) return
    if (get().saveStatus === 'conflict') return
    await startSave()
    if (dirty && get().saveStatus !== 'saved') throw new Error(get().saveError ?? 'The workflow could not be saved.')
  },

  retry() {
    if (get().saveStatus === 'error') void startSave()
  },

  async reload() {
    const id = get().id
    if (!id) return
    clearTimer()
    dirty = false
    nameDirty = false
    set({ phase: 'idle', id: null })
    await get().open(id)
  },

  async leave() {
    if (!get().id) return
    try {
      await get().flush()
    } catch {
      // Leaving is not blocked: the save status already told the user, and the
      // dashboard shows the last saved version.
    }
    clearTimer()
    dirty = false
    nameDirty = false
    set({ phase: 'idle', id: null, graph: null, name: '', saveStatus: 'saved', saveError: null, focusNodeId: null, ...clearHistory() })
  },

  clearFocus() {
    set({ focusNodeId: null })
  },
}))

/** Whether the open workflow has changes the server has not acknowledged. */
export function hasUnsavedWorkflow(): boolean {
  return dirty || inFlight !== null
}
